import React from 'react';
import { Loader2 } from 'lucide-react';

/**
 * Button Component - WCAG 2.1 AA Compliant
 * 
 * All color combinations meet 4.5:1 contrast ratio minimum
 * Focus states use 3:1 contrast for visibility
 * Disabled states use brand colors at reduced opacity (not gray)
 * 
 * Variants: primary, secondary, outline, ghost, danger, success, light (for dark backgrounds)
 * Sizes: sm, md, lg, xl
 */
const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  disabled = false,
  loading = false,
  fullWidth = false,
  icon: Icon,
  iconPosition = 'left',
  className = '',
  type = 'button',
  onClick,
  ...props
}) => {
  // Base styles with enhanced focus states
  const baseStyles = `
    inline-flex items-center justify-center font-semibold rounded-lg 
    focus:outline-none focus-visible:ring-4 focus-visible:ring-offset-2
    disabled:cursor-not-allowed
    active:scale-[0.98]
  `.trim();

  // WCAG AA compliant button variants
  const variants = {
    // Primary - Deep Blue background (7:1 contrast)
    primary: `
      bg-brand-700 text-white 
      hover:bg-brand-800 hover:shadow-xl hover:shadow-brand-700/30 hover:-translate-y-0.5
      focus-visible:ring-brand-600/30
      disabled:bg-brand-600 disabled:opacity-60 disabled:hover:shadow-none disabled:hover:translate-y-0
      shadow-sm
      transition-all duration-300 ease-out
    `,
    
    // Secondary - Light background with dark text (high contrast)
    secondary: `
      bg-gray-100 text-gray-900 
      hover:bg-gray-200 hover:shadow-md
      focus-visible:ring-gray-400/30
      disabled:bg-gray-50 disabled:text-gray-500 disabled:opacity-70
      border border-gray-200
    `,
    
    // Outline - Brand border with brand text
    outline: `
      bg-transparent border-2 border-brand-600 text-brand-700
      hover:bg-brand-50 hover:border-brand-700 hover:shadow-sm
      focus-visible:ring-brand-600/30
      disabled:border-brand-400 disabled:text-brand-400 disabled:opacity-60 disabled:hover:bg-transparent
    `,
    
    // Ghost - Minimal style for secondary actions
    ghost: `
      bg-transparent text-gray-700
      hover:bg-gray-100 hover:text-gray-900
      focus-visible:ring-gray-400/30
      disabled:text-gray-400 disabled:opacity-60 disabled:hover:bg-transparent
    `,
    
    // Danger - Error red (4.5:1 contrast)
    danger: `
      bg-error-600 text-white
      hover:bg-error-700 hover:shadow-lg
      focus-visible:ring-error-600/30
      disabled:bg-error-500 disabled:opacity-60 disabled:hover:shadow-none
      shadow-sm
    `,
    
    // Success - Green for positive actions
    success: `
      bg-success-600 text-white
      hover:bg-success-700 hover:shadow-lg
      focus-visible:ring-success-600/30
      disabled:bg-success-500 disabled:opacity-60 disabled:hover:shadow-none
      shadow-sm
    `,
    
    // Gradient - Brand gradient for premium CTAs
    gradient: `
      bg-gradient-to-r from-brand-600 to-accent-600 text-white
      hover:from-brand-700 hover:to-accent-700 hover:shadow-2xl hover:shadow-brand-600/40 hover:-translate-y-1
      focus-visible:ring-brand-600/30
      disabled:from-brand-500 disabled:to-accent-500 disabled:opacity-60 disabled:hover:shadow-none disabled:hover:translate-y-0
      shadow-md
      transition-all duration-300 ease-out
    `,
    
    // Light - For use on dark backgrounds (inverted contrast)
    light: `
      bg-white text-brand-700
      hover:bg-brand-50 hover:shadow-xl hover:shadow-white/20 hover:-translate-y-0.5
      focus-visible:ring-white/30
      disabled:bg-white/70 disabled:text-brand-500 disabled:opacity-70 disabled:hover:shadow-none disabled:hover:translate-y-0
      shadow-md
      transition-all duration-300 ease-out
    `,
    
    // Light outline - For use on dark backgrounds
    'light-outline': `
      bg-transparent border-2 border-white text-white
      hover:bg-white/10 hover:shadow-sm
      focus-visible:ring-white/30
      disabled:border-white/50 disabled:text-white/50 disabled:opacity-60 disabled:hover:bg-transparent
      backdrop-blur-sm
    `,
  };

  const sizes = {
    sm: 'px-3 py-1.5 text-sm gap-1.5 min-h-[32px]',
    md: 'px-5 py-2.5 text-sm gap-2 min-h-[40px]',
    lg: 'px-6 py-3 text-base gap-2.5 min-h-[48px]',
    xl: 'px-8 py-4 text-lg gap-3 min-h-[56px]',
  };

  const iconSizes = {
    sm: 'w-4 h-4',
    md: 'w-4 h-4',
    lg: 'w-5 h-5',
    xl: 'w-6 h-6',
  };

  return (
    <button
      type={type}
      disabled={disabled || loading}
      onClick={onClick}
      className={`
        ${baseStyles}
        ${variants[variant] || variants.primary}
        ${sizes[size]}
        ${fullWidth ? 'w-full' : ''}
        ${className}
      `}
      aria-disabled={disabled || loading}
      aria-busy={loading}
      {...props}
    >
      {loading ? (
        <>
          <Loader2 className={`${iconSizes[size]} animate-spin`} aria-hidden="true" />
          <span>Loading...</span>
        </>
      ) : (
        <>
          {Icon && iconPosition === 'left' && <Icon className={iconSizes[size]} aria-hidden="true" />}
          {children}
          {Icon && iconPosition === 'right' && <Icon className={iconSizes[size]} aria-hidden="true" />}
        </>
      )}
    </button>
  );
};

export default Button;
